import { Link } from 'react-router-dom'
import { ArrowRight, Shield, CheckCircle } from 'lucide-react'

export default function Hero() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-primary-700 via-primary-600 to-primary-700">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 sm:py-32 lg:px-8 lg:py-40">
        <div className="lg:grid lg:grid-cols-2 lg:gap-16 lg:items-center">
          {/* Content */}
          <div className="text-center lg:text-left">
            {/* Trust badge */}
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-medium text-primary-100 backdrop-blur-sm mb-6">
              <Shield className="h-4 w-4 text-accent-teal" aria-hidden="true" />
              <span>Trusted by Enterprise Security Teams</span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Clarity in Compliance
            </h1>

            {/* Subheadline */}
            <p className="mt-6 text-lg leading-8 text-primary-100 sm:text-xl lg:max-w-lg">
              Modern GRC solutions for enterprise teams who need to move fast without compromising security. Automate compliance, reduce audit prep time by 80%, and gain real-time visibility across your entire security program.
            </p>

            {/* Feature highlights */}
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:gap-6 justify-center lg:justify-start">
              <div className="flex items-center gap-2 text-primary-100">
                <CheckCircle className="h-5 w-5 text-accent-teal flex-shrink-0" aria-hidden="true" />
                <span className="text-sm">SOC 2 & ISO 27001</span>
              </div>
              <div className="flex items-center gap-2 text-primary-100">
                <CheckCircle className="h-5 w-5 text-accent-teal flex-shrink-0" aria-hidden="true" />
                <span className="text-sm">AI-Powered Automation</span>
              </div>
              <div className="flex items-center gap-2 text-primary-100">
                <CheckCircle className="h-5 w-5 text-accent-teal flex-shrink-0" aria-hidden="true" />
                <span className="text-sm">Enterprise Ready</span>
              </div>
            </div>

            {/* CTAs */}
            <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:gap-6 justify-center lg:justify-start">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent-teal px-6 py-3 text-base font-semibold text-primary-700 shadow-lg hover:bg-accent-mint transition-colors focus:outline-none focus:ring-2 focus:ring-accent-teal focus:ring-offset-2 focus:ring-offset-primary-700"
              >
                Get Started
                <ArrowRight className="h-5 w-5" aria-hidden="true" />
              </Link>
              <Link
                to="/features"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-white/10 px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-white/20 backdrop-blur-sm transition-colors border border-white/20 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary-700"
              >
                See How It Works
              </Link>
            </div>
          </div>

          {/* Visual element - abstract dashboard preview */}
          <div className="mt-16 lg:mt-0 hidden lg:block">
            <div className="relative">
              {/* Dashboard mockup */}
              <div className="relative rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-6 shadow-2xl">
                {/* Header bar */}
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-white/10">
                  <div className="flex gap-1.5">
                    <div className="h-3 w-3 rounded-full bg-red-400/80"></div>
                    <div className="h-3 w-3 rounded-full bg-yellow-400/80"></div>
                    <div className="h-3 w-3 rounded-full bg-green-400/80"></div>
                  </div>
                  <div className="flex-1 h-4 bg-white/10 rounded"></div>
                </div>

                {/* Compliance score card */}
                <div className="rounded-xl bg-white/10 p-4 mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm text-primary-200">Overall Compliance</div>
                    <div className="text-2xl font-bold text-accent-teal">94%</div>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full w-[94%] bg-gradient-to-r from-accent-teal to-accent-mint rounded-full"></div>
                  </div>
                </div>

                {/* Framework cards */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg bg-white/10 p-3">
                    <div className="text-xs text-primary-300 mb-1">SOC 2</div>
                    <div className="text-lg font-semibold text-white">98%</div>
                    <div className="h-1.5 bg-white/10 rounded-full mt-2 overflow-hidden">
                      <div className="h-full w-[98%] bg-accent-teal rounded-full"></div>
                    </div>
                  </div>
                  <div className="rounded-lg bg-white/10 p-3">
                    <div className="text-xs text-primary-300 mb-1">ISO 27001</div>
                    <div className="text-lg font-semibold text-white">92%</div>
                    <div className="h-1.5 bg-white/10 rounded-full mt-2 overflow-hidden">
                      <div className="h-full w-[92%] bg-accent-mint rounded-full"></div>
                    </div>
                  </div>
                  <div className="rounded-lg bg-white/10 p-3">
                    <div className="text-xs text-primary-300 mb-1">HIPAA</div>
                    <div className="text-lg font-semibold text-white">96%</div>
                    <div className="h-1.5 bg-white/10 rounded-full mt-2 overflow-hidden">
                      <div className="h-full w-[96%] bg-accent-teal rounded-full"></div>
                    </div>
                  </div>
                  <div className="rounded-lg bg-white/10 p-3">
                    <div className="text-xs text-primary-300 mb-1">GDPR</div>
                    <div className="text-lg font-semibold text-white">91%</div>
                    <div className="h-1.5 bg-white/10 rounded-full mt-2 overflow-hidden">
                      <div className="h-full w-[91%] bg-accent-mint rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating notification card */}
              <div className="absolute -bottom-6 -left-6 rounded-xl bg-white shadow-xl p-4 max-w-[200px] border border-navy-100">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-green-600" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-primary-700">Audit Ready</div>
                    <div className="text-xs text-navy-500">All controls verified</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom wave decoration */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg
          className="w-full h-16 sm:h-24"
          viewBox="0 0 1440 96"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
        >
          <path
            d="M0 96L60 90.7C120 85 240 75 360 69.3C480 64 600 64 720 69.3C840 75 960 85 1080 85.3C1200 85 1320 75 1380 69.3L1440 64V96H1380C1320 96 1200 96 1080 96C960 96 840 96 720 96C600 96 480 96 360 96C240 96 120 96 60 96H0Z"
            fill="white"
          />
        </svg>
      </div>
    </section>
  )
}
